import { useAppSelector } from '@app/store';
import { useMemo } from 'react';

import { DEFAULT_ACTIVE_WORD_COLOR, DEFAULT_VIDEO_BG_COLOR } from '../const';
import {
  selectAspectRatio,
  selectColorPresets,
  selectColorPresetsStatus,
  selectSelectedClip,
  selectVideoBackground,
} from '../selector';
import { ColorLabels } from '../theme/constants';
import { getCurrentPreset, getPresetColor } from '../utils';

/**
 * Hook to get colors from the current color preset
 * Color order:
 *   1st (BRAND-PRIMARY) → active word color
 *   2nd (NEUTRAL-PRIMARY) → video player background
 * Caption text color is always white (not from preset)
 * Uses clip-level preset_id when a clip is selected
 */
export const usePresetColors = () => {
  const selectedClip = useAppSelector(selectSelectedClip);
  const presets = useAppSelector(selectColorPresets);
  const presetsStatus = useAppSelector(selectColorPresetsStatus);

  const currentPreset = useMemo(
    () => getCurrentPreset(selectedClip?.preset_id, presets),
    [selectedClip?.preset_id, presets],
  );

  // 1st color (BRAND-PRIMARY) - used for active word color
  const activeWordPresetColor = useMemo(() => {
    if (presetsStatus === 'failed') return DEFAULT_ACTIVE_WORD_COLOR;

    return (
      getPresetColor(currentPreset, [ColorLabels['BRAND-PRIMARY']]) ||
      DEFAULT_ACTIVE_WORD_COLOR
    );
  }, [currentPreset, presetsStatus]);

  // 2nd color (NEUTRAL-PRIMARY) - used for video player background
  const backgroundPresetColor = useMemo(() => {
    if (presetsStatus === 'failed') return DEFAULT_VIDEO_BG_COLOR;

    return (
      getPresetColor(currentPreset, [
        ColorLabels['NEUTRAL-PRIMARY'],
        ColorLabels['BRAND-PRIMARY'],
      ]) || DEFAULT_VIDEO_BG_COLOR
    );
  }, [currentPreset, presetsStatus]);

  return {
    activeWordPresetColor,
    backgroundPresetColor,
    presetsStatus,
    currentPreset,
  };
};

/**
 * Hook to get portrait mode background styling
 * Uses preset colors and video background settings
 */
export const usePortraitBackground = () => {
  const aspectRatio = useAppSelector(selectAspectRatio);
  const videoBackground = useAppSelector(selectVideoBackground);
  const { backgroundPresetColor } = usePresetColors();

  const isPortrait = aspectRatio === '9_16';
  const isBlurMode = videoBackground?.blur ?? false;

  // Background color for portrait mode
  // selectVideoBackground handles: API background_color > lastUsedBackgroundColor > default
  // We only fall back to preset color if no color is set at all
  const portraitBgColor = useMemo(() => {
    return videoBackground?.color || backgroundPresetColor;
  }, [videoBackground?.color, backgroundPresetColor]);

  return {
    isPortrait,
    isBlurMode,
    portraitBgColor,
  };
};
